/** @jsxImportSource @emotion/react */
import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  fetchBatchOptions,
  fetchPaymentMethodOptions,
  fetchPaymentStatusOptions,
  fetchFollowUpStatusOptions,
} from 'helpers';

import Sidebar from 'components/layout/Sidebar/Sidebar';
import {
  FilterSection, FilterField, FilterActions, countActiveFilters,
} from 'components/layout/Sidebar/Filter';
import {
  CustomDatepicker as Datepicker,
  Select2,
  CreatableSelect,
} from 'components/common';
import interviewers from 'constants/interviewers';
import {
  filterConditionsContainerStyles,
  datepickerContainerStyles,
  select2ContainerStyles,
} from './Sidebar.styles';

import * as styles from './Sidebar.styles';

const FTPSidebar = ({
  search,
  startDate,
  callDate,
  endDate,
  callEndDate,
  setDateRange,
  setCallRange,
  filterCondition,
  setFilterCondition,
  handleSelect2Change,
  handleSubmit,
  handleReset,
}) => {
  const [options, setOptions] = useState({
    batchOptions: [],
    paymentMethodOptions: [],
    paymentStatusOptions: [],
    followUpStatusOptions: [],
  });

  const getFollowUpStatusOptions = () => fetchFollowUpStatusOptions().then((resp) => resp);

  useEffect(async () => {
    const [
      batchOptions,
      paymentMethodOptions,
      paymentStatusOptions,
      followUpStatusOptions,
    ] = await Promise.all([
      fetchBatchOptions(),
      fetchPaymentMethodOptions(),
      fetchPaymentStatusOptions(),
      getFollowUpStatusOptions(),
    ]);
    setOptions((prevState) => ({
      ...prevState,
      batchOptions,
      paymentMethodOptions,
      paymentStatusOptions,
      followUpStatusOptions,
    }));
  }, []);

  return (
    <Sidebar search={search} activeCount={countActiveFilters(filterCondition, startDate, callDate)}>
      <div css={filterConditionsContainerStyles}>
        <FilterSection title="Dates">
          <FilterField label="applied date">
            <Datepicker
              portalId="filter-panel-popover"
              containerStyles={datepickerContainerStyles}
              startDate={startDate}
              endDate={endDate}
              setDateRange={setDateRange}
              placeholder="date range"
            />
          </FilterField>
          <FilterField label="call date">
            <Datepicker
              portalId="filter-panel-popover"
              containerStyles={styles.datepickerContainerStyles}
              startDate={callDate}
              endDate={callEndDate}
              setDateRange={setCallRange}
              placeholder="call date range"
            />
          </FilterField>
        </FilterSection>
        <FilterSection title="Batch">
          <FilterField label="applied batch">
            <Select2
              portalMenu
              containerStyles={select2ContainerStyles}
              defaultValue={{ label: 'applied batch', value: '' }}
              options={options.batchOptions}
              placeholder="applied batch"
              onChange={(e) => handleSelect2Change(e, 'appliedBatch')}
              value={filterCondition.appliedBatch}
            />
          </FilterField>
          <FilterField label="enrolled batch">
            <Select2
              portalMenu
              containerStyles={select2ContainerStyles}
              defaultValue={{ label: 'enrolled batch', value: '' }}
              options={options.batchOptions}
              placeholder="enrolled batch"
              onChange={(e) => handleSelect2Change(e, 'enrolledBatch')}
              value={filterCondition.enrolledBatch}
            />
          </FilterField>
        </FilterSection>
        <FilterSection title="Payment">
          <FilterField label="payment type">
            <CreatableSelect
              portalMenu
              containerStyles={select2ContainerStyles}
              options={options.paymentMethodOptions}
              placeholder="payment type"
              value={filterCondition.paymentType}
              name="paymentType"
              filterCondition={filterCondition}
              setFilterCondition={setFilterCondition}
            />
          </FilterField>
          <FilterField label="payment status">
            <CreatableSelect
              portalMenu
              containerStyles={select2ContainerStyles}
              options={options.paymentStatusOptions}
              placeholder="payment status"
              value={filterCondition.paymentStatuses}
              name="paymentStatuses"
              filterCondition={filterCondition}
              setFilterCondition={setFilterCondition}
            />
          </FilterField>
        </FilterSection>
        <FilterSection title="Follow-up">
          <FilterField label="follow up status">
            <CreatableSelect
              portalMenu
              containerStyles={styles.select2ContainerStyles}
              options={options.followUpStatusOptions}
              placeholder="follow up status"
              value={filterCondition.followUpStatus}
              name="followUpStatus"
              filterCondition={filterCondition}
              setFilterCondition={setFilterCondition}
            />
          </FilterField>
          <FilterField label="follow up by">
            <CreatableSelect
              portalMenu
              filterCondition={filterCondition}
              setFilterCondition={setFilterCondition}
              containerStyles={styles.select2ContainerStyles}
              options={interviewers}
              placeholder="follow up by"
              value={filterCondition.followUpBy}
              name="followUpBy"
            />
          </FilterField>
        </FilterSection>
      </div>
      <FilterActions onApply={handleSubmit} onReset={handleReset} />
    </Sidebar>
  );
};

FTPSidebar.propTypes = {
  search: Sidebar.propTypes.search,
  startDate: PropTypes.instanceOf(Date),
  callDate: PropTypes.instanceOf(Date),
  endDate: PropTypes.instanceOf(Date),
  callEndDate: PropTypes.instanceOf(Date),
  setDateRange: PropTypes.func.isRequired,
  setCallRange: PropTypes.func.isRequired,
  filterCondition: PropTypes.shape({
    appliedBatch: PropTypes.oneOfType([PropTypes.string, PropTypes.shape()]),
    enrolledBatch: PropTypes.oneOfType([PropTypes.string, PropTypes.shape()]),
    paymentType: PropTypes.arrayOf(PropTypes.shape({})),
    application_result: PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string,
    }),
    test_result: PropTypes.string,
    followUpStatus: PropTypes.string,
    followUpBy: PropTypes.string,
    program: PropTypes.string,
    paymentStatuses: PropTypes.arrayOf(PropTypes.shape({})),
    search: PropTypes.string,
  }).isRequired,
  setFilterCondition: PropTypes.func.isRequired,
  // handleChange: PropTypes.func.isRequired,
  handleSelect2Change: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  handleReset: PropTypes.func.isRequired,
  // handleDownloadCSV: PropTypes.func.isRequired,
  // isCsvDownloading: PropTypes.bool.isRequired,
};

FTPSidebar.defaultProps = {
  search: null,
  startDate: null,
  endDate: null,
  callDate: null,
  callEndDate: null,
};

export default FTPSidebar;
